/**
 * Custom Autocomplete Dropdown Component
 * Similar to Funnel Analysis filter dropdowns
 */

import { useState, useRef, useEffect } from 'react';
import { ChevronDown, X } from 'lucide-react';

interface CustomAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder?: string;
  label: string;
  maxHeight?: string;
}

export const CustomAutocomplete: React.FC<CustomAutocompleteProps> = ({
  value,
  onChange,
  options,
  placeholder = 'Select or type...',
  label,
  maxHeight = 'max-h-80', // Increased from max-h-60 to show more options
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchValue, setSearchValue] = useState(value);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Sync searchValue with value prop
  useEffect(() => {
    setSearchValue(value);
  }, [value]);

  // Filter options based on search
  const filteredOptions = options.filter(option =>
    option.toLowerCase().includes(searchValue.toLowerCase())
  );

  const handleInputChange = (newValue: string) => {
    setSearchValue(newValue);
    onChange(newValue);
    setShowDropdown(true);
  };

  const handleOptionClick = (option: string) => {
    setSearchValue(option);
    onChange(option);
    setShowDropdown(false);
  };

  const handleClear = () => {
    setSearchValue('');
    onChange('');
    setShowDropdown(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
        {label}
      </label>
      
      <div className="relative">
        <input
          type="text"
          value={searchValue}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={() => setShowDropdown(true)}
          placeholder={placeholder}
          className="input pr-20"
        />
        
        {/* Clear button (X) - only show when there's a value */}
        {searchValue && (
          <button
            onClick={handleClear}
            className="absolute right-10 top-1/2 -translate-y-1/2 p-1 hover:bg-secondary-200 dark:hover:bg-secondary-700 rounded transition-colors"
            title="Clear filter"
          >
            <X className="w-4 h-4 text-secondary-500 dark:text-secondary-400" />
          </button>
        )}
        
        {/* Dropdown chevron */}
        <ChevronDown
          className={`absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400 transition-transform ${
            showDropdown ? 'rotate-180' : ''
          }`}
        />
      </div>

      {/* Dropdown List */}
      {showDropdown && filteredOptions.length > 0 && (
        <div className={`absolute z-30 mt-1 w-full ${maxHeight} overflow-y-auto bg-white dark:bg-secondary-800 border border-secondary-300 dark:border-secondary-600 rounded-lg shadow-lg`}>
          {filteredOptions.slice(0, 50).map((option, index) => (
            <button
              key={index}
              onClick={() => handleOptionClick(option)}
              className="w-full px-4 py-2 text-left text-sm text-secondary-700 dark:text-secondary-300 hover:bg-secondary-100 dark:hover:bg-secondary-700 transition-colors border-b border-secondary-100 dark:border-secondary-700 last:border-b-0"
            >
              {option}
            </button>
          ))}
          {filteredOptions.length > 50 && (
            <div className="px-4 py-3 text-xs text-secondary-500 dark:text-secondary-400 text-center border-t border-secondary-200 dark:border-secondary-700 bg-secondary-50 dark:bg-secondary-900 sticky bottom-0">
              <strong>Showing first 50 of {filteredOptions.length} results</strong>
              <div className="mt-1 text-xs">Type to narrow down results</div>
            </div>
          )}
        </div>
      )}

      {/* No results message */}
      {showDropdown && searchValue && filteredOptions.length === 0 && (
        <div className="absolute z-30 mt-1 w-full bg-white dark:bg-secondary-800 border border-secondary-300 dark:border-secondary-600 rounded-lg shadow-lg p-4 text-center text-sm text-secondary-500">
          No matches found
        </div>
      )}
    </div>
  );
};

